<template>
  <div class="product-listing-card-container">
    <UiProductCard
      v-bind="productCardProps"
      @add-to-bag="onAddToBag"
      @add-to-waitlist="onAddToWaitlist"
      @add-to-wishlist="onAddToWishlist"
      @remind-me="onRemindMe"
      @click="onProductCardClick"
    />
  </div>
</template>

<script setup lang="ts">
import UiProductCard, { type ProductProps } from '@sephora-asia/ui-product-card'
import { useQuery, useMutation, useIsMutating } from '@tanstack/vue-query'
import { isEmpty } from 'lodash-es'
import { useAddToBag } from '~/composables/query/cart/use-bag-mutation'
import { useUpdateWaitlistVariantMutation } from '~/composables/query/waitlist/use-waitlist-mutations'
import { useGetWaitlistQuery } from '~/composables/query/waitlist/use-waitlist-query'
import { useCreateRemindMeVariantMutation } from '~/composables/query/notification-subscription/use-remind-me-mutations'
import { useGetRemindMeQuery } from '~/composables/query/notification-subscription/use-remind-me-query'
import {
  LAUNCH_CONFIG_PLATFORM,
  LaunchStatus,
} from '~/composables/utils/product-listing/card-container/constants'

import { useTrackingHelper } from '~/composables/utils/tracking/use-tracking-helpers'
import type { ProductListingCardContainerExtendedProps } from '~/types/components/product-listing.types'

const props = defineProps<ProductListingCardContainerExtendedProps>()

const { countryCode } = useI18nLocale()
const { isLoggedIn, currentUser } = await useCurrentUser()
const { currentRoute, options, replace } = useRouter()
const pageMetaStore = usePageMetaStore()
const { t } = useI18n()
const { trackEvent } = useGtmTrack()
const { carouselOriginPageType } = useTrackingHelper()

const userId = computed(() => currentUser.value?.id)

const pendingActionVariantId = computed(
  () =>
    (currentRoute.value.query?.waitlistId ||
      currentRoute.value.query?.wishlistId ||
      currentRoute.value.query?.remindMeVariantId) as string | undefined
)

const totalActiveMutations = useIsMutating({
  predicate: mutation => {
    const shortMutationKey = mutation.options.mutationKey?.[0] as string
    return [
      getAddWaitlistMutationKey()[0],
      getAddWishlistMutationKey()[0],
      getRemindMeMutationKey()[0],
    ].includes(shortMutationKey)
  },
})

const isMutationInProgress = computed(() => {
  return totalActiveMutations.value > 0
})

const {
  data: wishlistedVariantsIds,
  suspense: fetchWishlistedVariantsIdsSuspense,
} = useQuery({
  ...useGetWishlistQuery(userId),
  enabled: isLoggedIn,
})

const {
  data: waitlistedVariantsIds,
  suspense: fetchWaitlistedVariantsIdsSuspense,
} = useQuery({
  ...useGetWaitlistQuery(userId, {
    query: { 'page[size]': 500, 'page[number]': 1 },
  }),
  enabled: isLoggedIn,
})

const {
  data: notificationSubscribedVariantsIds,
  suspense: fetchNotificationSubscribedVariantsIdsSuspense,
} = useQuery<string[]>({
  ...useGetRemindMeQuery(userId, {
    query: { 'page[size]': 500, 'page[number]': 1 },
  }),
  enabled: isLoggedIn,
})

if (isLoggedIn.value) {
  await Promise.allSettled([
    fetchWishlistedVariantsIdsSuspense(),
    fetchWaitlistedVariantsIdsSuspense(),
    fetchNotificationSubscribedVariantsIdsSuspense(),
  ])
}

const { isPending: isAddingToBag, mutate: addToBag } = useMutation(
  useAddToBag(userId)
)

const { mutate: addToWishlist } = useMutation(
  useUpdateWishlistVariantMutation(props.productData.productName, userId)
)

const { mutate: addToWaitlist, isPending: isWaitlisting } = useMutation(
  useUpdateWaitlistVariantMutation(userId)
)

const { mutate: remindMeMutate, isPending: isRemindMeDisabled } = useMutation(
  useCreateRemindMeVariantMutation(userId)
)

const isQualifiedUser = computed(() => {
  const userTier = currentUser.value?.loyaltyTier

  return (
    isLoggedIn.value &&
    userTier &&
    props.productData.launchConfig?.prelaunch?.tiers?.includes(userTier)
  )
})

const isValidPlatform = computed(() => {
  return props.productData.launchConfig?.prelaunch?.platforms?.some(
    (platform: string) =>
      LAUNCH_CONFIG_PLATFORM.includes(
        platform as (typeof LAUNCH_CONFIG_PLATFORM)[number]
      )
  )
})

const isAppExclusive = computed(() => {
  const { launchStatus } = props.productData

  if (launchStatus === LaunchStatus.Prelaunch) {
    return isQualifiedUser.value && !isValidPlatform.value
  }

  return false
})

const badgeText = computed(() => {
  const { launchStatus } = props.productData

  if (launchStatus === LaunchStatus.Preview)
    return t('products.badgeText.comingSoon')

  if (launchStatus === LaunchStatus.Prelaunch) {
    return isQualifiedUser.value
      ? t('products.badgeText.priorityAccess')
      : t('products.badgeText.comingSoon')
  }

  return ''
})

const showRemindMe = computed(() => {
  if (outOfStock.value && isAppExclusive.value) {
    return false
  }
  const { launchStatus } = props.productData

  if (launchStatus === LaunchStatus.Preview) return true

  if (launchStatus === LaunchStatus.Prelaunch) {
    return !(isQualifiedUser.value && isValidPlatform.value)
  }
  return false
})

const isComingSoonProduct = computed(() => {
  const { launchStatus } = props.productData

  return (
    launchStatus === LaunchStatus.Preview ||
    (launchStatus === LaunchStatus.Prelaunch && !isQualifiedUser.value)
  )
})

const outOfStock = computed(
  () => props.productData.soldOut && !isComingSoonProduct.value
)

const productCardProps = computed(
  () =>
    ({
      ...props.productData,
      productId: props.productData.id,
      image: {
        src: props.productData.imageUrl,
      },
      // Inventory is irrelevant for Preview products (all users) and Prelaunch products (non-qualified users).
      // So the OOS label should not appear on Coming Soon products.
      soldOut: outOfStock.value,
      featuredVariantId: props.productData?.featuredVariantId,
      isLoggedIn: isLoggedIn.value,
      isWaitlisting: isWaitlisting.value,
      isAddingToBag: isAddingToBag.value,
      badgeText: badgeText.value,
      showRemindMe: showRemindMe.value,
      isRemindMeDisabled: isAppExclusive.value || isRemindMeDisabled.value,
      wishlistedVariantIds: wishlistedVariantsIds.value ?? [],
      waitlistedVariantIds: waitlistedVariantsIds.value ?? [],
      notificationSubscribedVariantIds:
        notificationSubscribedVariantsIds.value ?? [],
      label: {
        ...(outOfStock.value
          ? {
              outOfStock: t('products.dictionary.oos'),
              waitlisted: t('products.dictionary.waitlisted'),
              waitlisting: t('products.dictionary.waitlistMe'),
              waitlistMe: t('products.dictionary.waitlistMe'),
            }
          : {
              addToBag: t('products.dictionary.addToBag'),
              addingToBag: t('products.dictionary.addingToBag'),
            }),
        ...(!isEmpty(props.productData.ad) && {
          selectedTypesOnly: t(
            `products.ad.${props.productData.optionType}Only`
          ),
        }),
        ...{
          remindMe: isAppExclusive.value
            ? t('products.dictionary.appExclusive')
            : t('products.dictionary.remindMe'),
        },
      },
    }) as ProductProps
)

function onAddToBag(variantId: string) {
  addToBag({ variantId, quantity: 1 })

  trackAddItemEvent(variantId)
}

function onAddToWaitlist(variantId: string) {
  if (!isLoggedIn.value) return handleSignInRedirect('waitlistId', variantId)

  addToWaitlist(variantId)
}

function onAddToWishlist(variantId: string) {
  if (!isLoggedIn.value) return handleSignInRedirect('wishlistId', variantId)

  addToWishlist(variantId)
}

function onRemindMe(variantId: string) {
  if (!isLoggedIn.value)
    return handleSignInRedirect('remindMeVariantId', variantId)

  const { eventType, eventName } = getEventDetails()

  if (eventType && eventName) {
    remindMeMutate({ variantId, eventName, eventType })
  }
}

function getEventDetails() {
  const type = isQualifiedUser.value ? 'prelaunch' : 'launch'

  return {
    eventType: `products_${type}`,
    eventName: props.productData.launchConfig?.[type]?.name || '',
  }
}

function handleSignInRedirect(param: string, value: string) {
  const currentPath = useRequestURL()
  currentPath.searchParams.set(param, value)

  return redirectToSignIn({
    return_to: currentPath.pathname + currentPath.search + currentPath.hash,
  })
}

function onProductCardClick() {
  let origin = options.history.state.back

  if (!origin) {
    try {
      origin = new URL(document.referrer, useRequestURL().href).pathname
    } catch {
      origin = currentRoute.value.path
    }
  }

  trackEvent({
    event: 'product_click',
    product_id: props.productData.id,
    variant_id: props.productData.featuredVariantId,
    product_tag: props.productData.tags.join(' | '),
    product_highlight: '', // TODO: No idea why empty string. To review whether this is required or not
    product_seal: props.productData.seal,
    origin,
    page_path: currentRoute.value.path,
    campaign_name: pageMetaStore.campaignName,
    card_sequence_num: props.productCardIndex + 1,
    status: props.productData.launchStatus,
    filter_type: badgeText.value,
    ...(props.carouselName ? getRecommendationCarouselParams() : {}),
  })
}

function trackAddItemEvent(variantId: string) {
  trackEvent({
    event: props.carouselName ? 'add_to_cart' : 'Add to Cart',
    product_id: props.productData.id,
    product_name: props.productData.productName,
    variant_id: variantId,
    brand: props.productData.brandName,
    price: props.productData.price,
    quantity: 1,
    sku: props.productData.sapSku,
    ...(props.carouselName ? getRecommendationCarouselParams() : {}),
  })
}

function getRecommendationCarouselParams() {
  /*
  recommendation_strategy will only be passed if source is recommendation_api i.e. Potion
  for the rest of the carousels the value will be carouselOriginPageType itself for e.g. "homepage", "brand_sephora-collection", "category_makeup", "pages/christmas-gift-ideas"
  */
  return {
    origin: `${carouselOriginPageType.value}${props.productData.recommendationStrategy ? `|${props.productData.recommendationStrategy}` : ''}`,
    recommendation_id: props.productData.recommendationId,
    carousel_name: props.carouselName,
    card_number: currentUser.value?.cardNumber,
    session_id: useCookie('_ga_6KSFTBFXP6').value?.split('.')[2],
    user_id: userId.value,
    site_country: countryCode.toLowerCase(),
    traffic_split_key: props.productData.recommendationSplitKey,
  }
}

function performPendingActionAfterSignIn() {
  const query = currentRoute.value.query

  if (
    isLoggedIn.value &&
    !isMutationInProgress.value &&
    pendingActionVariantId.value &&
    pendingActionVariantId.value === props.productData.featuredVariantId
  ) {
    // Determine if it's a wishlist, waitlist, or remind-me action
    let queryAttribute = ''

    if (query?.wishlistId) {
      queryAttribute = 'wishlistId'
      addToWishlist(pendingActionVariantId.value)
    } else if (query?.waitlistId) {
      queryAttribute = 'waitlistId'
      addToWaitlist(pendingActionVariantId.value)
    } else if (query?.remindMeVariantId) {
      queryAttribute = 'remindMeVariantId'
      const { eventType, eventName } = getEventDetails()

      if (eventType && eventName) {
        remindMeMutate({
          variantId: pendingActionVariantId.value,
          eventName,
          eventType,
        })
      }
    }

    if (queryAttribute) {
      const newQuery = Object.assign({}, query) as Record<string, string>

      // Remove the action query parameter after performing the action
      delete newQuery?.[queryAttribute]
      replace({ query: newQuery })
    }
  }
}

onMounted(performPendingActionAfterSignIn)
</script>
